import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEditorFileHandlers } from 'src/common/components/Editor';
import { LanguageKeys } from 'src/common/constants/languages';
import { createDefaultHomeDocument } from 'src/helpers/pageDocumentUtils';
import {
  addPageBlock,
  getBodyBlocks,
  getHeroBlocks,
  IPageDocument,
  moveBlockOrder,
  movePageBlock,
  normalizePageDocument,
  pageContentFromDocument,
  removePageBlock,
  updatePageBlock,
} from 'src/helpers/pageDocumentUtils';
import { syncHeroSectionFileUsage, syncPageContentTextFileUsage } from 'src/helpers/pageContentFileUsage';
import { assignBlockSortOrders } from 'src/helpers/pageContentUtils';
import { IHeroSettings } from 'src/interfaces/HeroSettings';
import { IPageBlock, PageBlockType } from 'src/interfaces/PageBlock';
import { IPageContent } from 'src/interfaces/PageContent';
import type { AddBlockLocation } from 'src/helpers/pageDocumentUtils';
import useFileManagerStore from 'src/stores/fileManagerStore';
import usePageContentsStore from 'src/stores/pageContentsStore';

export default function usePageBlocksContent(contentKey: string) {
  const { pageContentsByName, savePageContent, isLoading } = usePageContentsStore(s => s);
  const pageContent = pageContentsByName?.[contentKey] as IPageContent | undefined;

  const [document, setDocumentState] = useState<IPageDocument>(() =>
    normalizePageDocument(pageContent),
  );
  const [selectedEditorLang, setSelectedEditorLang] = useState<LanguageKeys>(LanguageKeys.en);

  useEffect(() => {
    setDocumentState(normalizePageDocument(pageContent));
    // Re-sync when persisted identity changes so in-progress local edits are not wiped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey, pageContent?.id, pageContent?.updatedAt]);

  const setDocument = useCallback((next: IPageDocument) => {
    setDocumentState(next);
  }, []);

  const { clearPendingFiles } = useEditorFileHandlers(
    'pageContent',
    pageContent?.id || contentKey,
    'content',
  );
  const markFileUsage = useFileManagerStore(s => s.markFileUsage);
  const resolveFileIdsByUrls = useFileManagerStore(s => s.resolveFileIdsByUrls);
  const markPendingFilesForContent = useFileManagerStore(s => s.markPendingFilesForContent);

  const blocks = useMemo(() => getBodyBlocks(document), [document]);
  const heroBlocks = useMemo(() => getHeroBlocks(document), [document]);
  const heroSettings = document.heroSettings;

  const hero = useMemo(
    () =>
      heroBlocks.length || heroSettings
        ? { ...heroSettings, blocks: heroBlocks }
        : undefined,
    [heroBlocks, heroSettings],
  );

  const save = async () => {
    const payload = pageContentFromDocument(document, pageContent, contentKey);
    const previousDoc = normalizePageDocument(pageContent);
    await savePageContent(payload, pageContent?.id);
    const contentId = pageContent?.id || contentKey;
    await markPendingFilesForContent('pageContent', contentId);
    await syncHeroSectionFileUsage(
      markFileUsage,
      contentId,
      previousDoc,
      document,
      {
        additionalContentIdsForUnmark: contentId === contentKey ? [] : [contentKey],
        markAddedRefs: false,
      },
    );
    await syncPageContentTextFileUsage(
      markFileUsage,
      resolveFileIdsByUrls,
      contentId,
      previousDoc,
      document,
      {
        previousLegacySections: pageContent?.content,
        nextLegacySections: pageContent?.content,
        additionalContentIdsForUnmark: contentId === contentKey ? [] : [contentKey],
      },
    );
  };

  const setHeroSettings = useCallback((next: IHeroSettings | undefined) => {
    setDocumentState(prev => ({ ...prev, heroSettings: next }));
  }, []);

  const setHero = useCallback(
    (next: { blocks?: IPageBlock[] } & IHeroSettings | undefined) => {
      if (!next) {
        setDocumentState(prev => ({
          ...prev,
          heroSettings: undefined,
          blocks: getBodyBlocks(prev),
        }));
        return;
      }
      const { blocks: heroBlockList, ...settings } = next;
      setDocumentState(prev => {
        const body = getBodyBlocks(prev);
        const hero = heroBlockList ?? getHeroBlocks(prev);
        return {
          heroSettings: settings,
          blocks: [...hero.map(b => ({ ...b, zone: 'hero' as const })), ...body],
        };
      });
    },
    [],
  );

  const addBlockAt = (
    location: AddBlockLocation,
    type: PageBlockType,
    options?: { trackMapId?: string; columnCount?: 2 | 3 | 4 },
  ) => {
    setDocumentState(prev => addPageBlock(prev, location, type, options));
  };

  const addBlock = (block: IPageBlock) => {
    setDocumentState(prev => {
      const body = assignBlockSortOrders([
        ...getBodyBlocks(prev),
        { ...block, zone: 'body' },
      ]);
      return { ...prev, blocks: [...getHeroBlocks(prev), ...body] };
    });
  };

  const addTextBlock = () => {
    setDocumentState(prev =>
      addPageBlock(prev, { kind: 'zone', zone: 'body' }, 'text'),
    );
  };

  const removeBlock = (id: string) => {
    setDocumentState(prev => removePageBlock(prev, id));
  };

  const updateBlock = (id: string, patch: Partial<IPageBlock>) => {
    setDocumentState(prev => updatePageBlock(prev, id, patch));
  };

  const moveBlock = (id: string, direction: 'up' | 'down') => {
    setDocumentState(prev => moveBlockOrder(prev, id, direction));
  };

  const reorderBlocks = (next: IPageBlock[]) => {
    setDocumentState(prev => ({
      ...prev,
      blocks: [
        ...getHeroBlocks(prev),
        ...next.map(b => ({ ...b, zone: 'body' as const })),
      ],
    }));
  };

  const moveBlockByDrag = (activeId: string, overId: string) => {
    setDocumentState(prev => movePageBlock(prev, activeId, overId));
  };

  const addHeroBlock = (block: IPageBlock) => {
    setDocumentState(prev => ({
      ...prev,
      blocks: [
        ...getHeroBlocks(prev),
        { ...block, zone: 'hero' },
        ...getBodyBlocks(prev),
      ],
    }));
  };

  const removeHeroBlock = (id: string) => {
    setDocumentState(prev => removePageBlock(prev, id));
  };

  const updateHeroBlock = (id: string, patch: Partial<IPageBlock>) => {
    setDocumentState(prev => updatePageBlock(prev, id, patch));
  };

  const moveHeroBlockOrder = (id: string, direction: 'up' | 'down') => {
    setDocumentState(prev => moveBlockOrder(prev, id, direction));
  };

  const reorderHeroBlocks = (next: IPageBlock[]) => {
    setDocumentState(prev => ({
      ...prev,
      blocks: [...next.map(b => ({ ...b, zone: 'hero' as const })), ...getBodyBlocks(prev)],
    }));
  };

  const initDefaultHero = useCallback(() => {
    const defaults = createDefaultHomeDocument();
    setDocumentState(prev => ({
      blocks: [...defaults.blocks, ...getBodyBlocks(prev)],
      heroSettings: defaults.heroSettings,
    }));
  }, []);

  return {
    pageContent,
    document,
    setDocument,
    blocks,
    hero,
    heroSettings,
    heroBlocks,
    setHero,
    setHeroSettings,
    setBlocks: (next: IPageBlock[]) => reorderBlocks(next),
    selectedEditorLang,
    setSelectedEditorLang,
    save,
    addBlock,
    addBlockAt,
    addTextBlock,
    removeBlock,
    updateBlock,
    moveBlock,
    moveBlockByDrag,
    reorderBlocks,
    addHeroBlock,
    removeHeroBlock,
    updateHeroBlock,
    moveHeroBlockOrder,
    reorderHeroBlocks,
    initDefaultHero,
    isLoading,
    clearPendingFiles,
  };
}
